import { useEffect, useRef } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { useLoggedInUser } from '@ApplicationWide/AppConfig';
import { getUrlWithoutSearchParameter, getUrlSearchParameters } from '@Utils/urls';
import { PREVIEW_NEW_HIRE_EXPERIENCE_PATH } from './routes/non-admin/non-admin-urls';
import useMasqueradeState from './MasqueradeBanner/useMasqueradeState';
import { usePreAuthConfig } from './auth/usePreAuthConfig';

const INVITE_URL = '/preboarding';
const COMPANY_INFO_URL = '/preboarding/company-info';
const HOME_PAGE_URL = '/';
const POST_PREBOARDING_STAGES = [
  'pre_start',
  'first_week',
  'first_month',
  'ramping_up',
  'offboarding',
  'departed',
  'incomplete',
  'registered',
  'no_activity',
  'last_month',
  'last_week',
];

const isPreboardingPath = (pathname) => pathname.startsWith('/preboarding');

function preboardingRedirects(currentStage, pathname, history, isPreviewing, idsAuthenticationFeatureFlag) {
  // user has not yet set password and must do so before anything else
  // in case of IDS6, user has already set the password via IDS6 so no point in setting the password here
  if (currentStage === 'invited' && pathname !== INVITE_URL) {
    const destinationUrl = idsAuthenticationFeatureFlag ? COMPANY_INFO_URL : INVITE_URL;
    history.push(destinationUrl);
  }

  // user is preboarding and cannot access the full app
  if (currentStage === 'preboarding' && !isPreboardingPath(pathname)) {
    history.push(COMPANY_INFO_URL);
  }

  // user is fully boarded and can no longer preboard
  if (POST_PREBOARDING_STAGES.includes(currentStage) && isPreboardingPath(pathname) && !isPreviewing) {
    history.push(HOME_PAGE_URL);
  }
}

export function invitedPathRedirects(currentStage, history) {
  const { inviteId } = getUrlSearchParameters(window.location.href);
  if (inviteId) {
    // the inviteId is sensitive, so we immediately hide it
    // by modifying the browser URL (this doesn't trigger a page reload!)
    const newUrl = getUrlWithoutSearchParameter(window.location.href, 'inviteId');
    window.history.pushState(undefined, '', newUrl);

    if (currentStage === 'preboarding') {
      history.push(COMPANY_INFO_URL);
    }
  }
}

const checkRedirects = (
  currentStage,
  pathname,
  history,
  isPreviewing,
  isMasquerading,
  idsAuthenticationFeatureFlag,
) => {
  // do not allow preboarding when masquerading
  if (isMasquerading && !isPreviewing) {
    if (isPreboardingPath(pathname)) {
      history.push(HOME_PAGE_URL);
    }
    return;
  }

  invitedPathRedirects(currentStage, history);
  preboardingRedirects(currentStage, pathname, history, isPreviewing, idsAuthenticationFeatureFlag, isMasquerading);
};

export default function useRedirectBasedOnUserStatus(onlyOnInit) {
  const preAuthConfig = usePreAuthConfig();
  const { pathname } = useLocation();
  const history = useHistory();
  const runCount = useRef(0);
  const { user } = useLoggedInUser();
  const { isMasquerading } = useMasqueradeState();
  const match = useRouteMatch(PREVIEW_NEW_HIRE_EXPERIENCE_PATH);
  const isPreviewing = match?.isExact;

  useEffect(() => {
    if (!user || (onlyOnInit && runCount.current > 0)) return;

    runCount.current++;
    const { currentStage } = user;

    if (user.isResetPasswordTokenSet && user.provider?.includes('email')) {
      if (!pathname.includes('reset_password')) {
        history.push('/reset_password');
      }
    } else {
      checkRedirects(
        currentStage,
        pathname,
        history,
        isPreviewing,
        isMasquerading,
        preAuthConfig.idsAuthenticationFeatureFlag,
      );
    }
  }, [
    pathname,
    history,
    user,
    runCount,
    onlyOnInit,
    isPreviewing,
    isMasquerading,
    preAuthConfig.idsAuthenticationFeatureFlag,
  ]);
}
